import { APPOINTMENT_CANCELLED_BY_TYPE, APPOINTMENT_STATUS_TYPE, DAYS_OF_WEEK_TYPE, LANGUAGES_SUPPORTED, LanguagesSupported, REVIEW_STATUS_TYPE, PICK_UP_PRICE_TYPE, REPRESENTATIVE_ROLE_TYPE, SHOP_CATEGORY_TYPE, SHOP_OWNER_TYPE, SHOP_RATING_TYPE, SHOP_SERVICE_NAME_TYPE, SHOP_SERVICE_TYPE, SHOP_STATUS_TYPE, USER_STATUS_TYPE, FORUM_TYPE, TOPIC_TYPE, TOPIC_STATUS_TYPE, MONTH_OF_YEAR_TYPE, NEWS_CATEGORY_TYPE, NEWS_STATUS_TYPE, TOPIC_CREATOR_TYPE } from '@/types/enums';
import { LanguageInfo } from '@/types/language';
import { Route } from '@/types/routes';
import { PaginationState } from '@tanstack/react-table';
import { enUS, vi } from 'date-fns/locale';

// #region Routes
enum RoutesKey {
    home,
    dashboard,
    signIn,
    forgotPassword,
    verifyOtp,
    resetPassword,
    changePassword,
    userManagement,
    shopManagement,
    appointmentManagement,
    reviewManagement,
    promotionManagement,
    promotionCreate,
    promotionEdit,
    promotionClone,
    contentSetting,
    aboutUs,
    miaPolicy,
    privacyPolicy,
    forum,
    news,
    newsCreate,
    newsEdit,
    editUser,
    editShop,
    appointmentDetail,
    viewReview,
    editTopic,
    topicCreate,
    referralCodes,
    seoTool,
    notificationManagement
};

export const ROUTES: Record<keyof typeof RoutesKey, Route> = {
    home: {
        href: '/',
        title: 'words_title.home',
        menuLabel: 'words_title.home'
    },
    dashboard: {
        href: '/dashboard',
        title: 'words_title.dashboard',
        menuLabel: 'words_title.dashboard'
    },
    signIn: {
        href: '/sign-in',
        menuLabel: ''
    },
    forgotPassword: {
        href: '/forgot-password',
        menuLabel: ''
    },
    verifyOtp: {
        href: '/verify-otp',
        menuLabel: ''
    },
    resetPassword: {
        href: '/reset-password',
        menuLabel: ''
    },
    changePassword: {
        href: '/change-password',
        menuLabel: ''
    },
    userManagement: {
        href: '/users',
        title: 'words_title.user_management',
        menuLabel: 'words_title.user_management'
    },
    editUser: {
        href: '/users/:code',
        title: 'words_title.edit_user',
        menuLabel: 'words_title.edit_user'
    },
    shopManagement: {
        href: '/shops',
        title: 'words_title.shop_management',
        menuLabel: 'words_title.shop_management'
    },
    editShop: {
        href: '/shops/:code',
        title: 'words_title.edit_shop',
        menuLabel: 'words_title.edit_shop'
    },
    appointmentManagement: {
        href: '/appointment-management',
        title: 'words_title.appointment_management',
        menuLabel: 'words_title.appointment_management'
    },
    appointmentDetail: {
        href: '/appointment-management/:code',
        title: 'words_title.appointment_detail',
        menuLabel: 'words_title.appointment_detail'
    },
    reviewManagement: {
        href: '/reviews',
        title: 'words_title.review_management',
        menuLabel: 'words_title.review_management'
    },
    promotionManagement: {
        href: '/promotion',
        title: 'words_title.promotion_management',
        menuLabel: 'words_title.promotion_management'
    },
    promotionCreate: {
        href: '/promotion-create',
        title: 'words_title.promotion_create',
        menuLabel: 'words_title.promotion_create'
    },
    promotionEdit: {
        href: '/promotion-edit',
        title: 'words_title.promotion_edit',
        menuLabel: 'words_title.promotion_edit'
    },
    promotionClone: {
        href: '/promotion-clone',
        title: 'words_title.promotion_create',
        menuLabel: 'words_title.promotion_create'
    },
    contentSetting: {
        href: '/content-setting',
        title: 'words_title.content_setting',
        menuLabel: 'words_title.content_setting'
    },
    aboutUs: {
        href: '/content-setting/about-miapet',
        title: 'words_title.about_miapet',
        menuLabel: 'words_title.about_miapet'
    },
    miaPolicy: {
        href: '/content-setting/miapet-policy',
        title: 'words_title.miapet_policies',
        menuLabel: 'words_title.miapet_policies'
    },
    privacyPolicy: {
        href: '/content-setting/privacy-policy',
        title: 'words_title.privacy_policies',
        menuLabel: 'words_title.privacy_policies'
    },
    forum: {
        href: '/content-setting/forum',
        title: 'words_title.forum',
        menuLabel: 'words_title.forum'
    },
    news: {
        href: '/content-setting/news',
        title: 'words_title.news',
        menuLabel: 'words_title.news'
    },
    newsCreate: {
        href: '/content-setting/news-create',
        title: 'words_title.news_create',
        menuLabel: 'words_title.news_create'
    },
    newsEdit: {
        href: '/content-setting/news:code',
        title: 'words_title.news_edit',
        menuLabel: 'words_title.news_edit'
    },
    viewReview: {
        href: '/reviews/:code',
        title: 'words_title.view_review',
        menuLabel: 'words_title.view_review'
    },
    editTopic: {
        href: '/content-setting/forum:code',
        title: 'words_title.edit_topic',
        menuLabel: 'words_title.edit_topic'
    },
    topicCreate: {
        href: '/content-setting/forum-create',
        title: 'words_title.topic_create',
        menuLabel: 'words_title.topic_create'
    },
    referralCodes: {
        href: '/referralCodes',
        title: 'words_title.referral_codes',
        menuLabel: 'words_title.referral_codes'
    },
    seoTool: {
        href: '/seoTool',
        title: 'words_title.seo_tool',
        menuLabel: 'words_title.seo_tool'
    },
    notificationManagement: {
        href: '/notifications',
        title: 'words_title.notification_list',
        menuLabel: 'words_title.notification_list'
    }
};

// #endregion Routes

// #region Languages
export const DEFAULT_LANGUAGE: LanguagesSupported = LANGUAGES_SUPPORTED.English;

export const LANGUAGES_MAPPER: {
    english: LanguageInfo,
    vietnamese: LanguageInfo
} = {
    english: {
        code: LANGUAGES_SUPPORTED.English,
        fullname: 'words_title.english',
        shortName: 'EN',
        flag: 'gb',
        dateFns: enUS
    },
    vietnamese: {
        code: LANGUAGES_SUPPORTED.Vietnamese,
        fullname: 'words_title.vietnamese',
        shortName: 'VI',
        flag: 'vn',
        dateFns: vi
    }
};

// #endredion

// #region Dates

export const DATE_YMD = 'yyyy/MM/dd';
export const DATE_YMD_HMS = 'YYYY/MM/DD HH:mm:ss';
export const DATE_YMD_HMS_12H_FORMAT = 'DD/MM/YYYY hh:mm:ss A';
// use for display UI
export const DISPLAY_FNS_DATE_YMD = 'dd/MM/yyyy';
export const DISPLAY_FNS_DATE_YMD_FOR_LABEL_CALENDAR = 'yyyy.MM';
export const DISPLAY_MOMENT_DATE_YMD = 'DD/MM/YYYY';
export const DISPLAY_MOMENT_TIME_HMS = 'HH:mm:ss';
export const DISPLAY_MOMENT_TIME_HM_AM_PM = 'hh:mm A';
export const DISPLAY_MOMENT_DATE_TIME_HM = 'DD/MM/YYYY hh:mm A';
export const DISPLAY_MOMENT_TIME_HM_AM_PM_2 = 'hh:mm:A';
export const DISPLAY_MOMENT_TIME_HM = 'HH:mm';

// use for submit api
export const SUBMIT_MOMENT_DATE_YMD = 'YYYY-MM-DD';
export const SUBMIT_MOMENT_DATE_YM = 'YYYY-MM';
export const SUBMIT_MOMENT_DATE_Y = 'YYYY';

// #endregion

// #region Table

export const DEFAULT_SELECT_PAGE_SIZES: number[] = [10, 20, 50, 100, 200, 500, 1000];

export const DEFAULT_PAGINATION: PaginationState = {
    pageIndex: 0,
    pageSize: 10
};

// #endregion

// #region Regex
export const PHONE_VALIDATE_REGEX = /^0\d{9}$/;
export const PASSWORD_VALIDATE_REGEX = /^(?=.*[A-Z])(?=.*[@#$%^&+=!])[A-Za-z\d@#$%^&+=!]{8,}$/;
export const TIME_FORMAT_24h_STEP_5M = /^(?:[01]\d|2[0-3]):[0-5](0|5)$/;
export const COORDINATE_REGEX = /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?((1[0-7]\d|\d{1,2})(\.\d+)?|180(\.0+)?)$/;

// #endregion

// #region number
export const MAX_LENGTH_DEFAULT_INPUT = 255;

export const MAX_LENGTH_DEFAULT_NOTE = 2000;

export const MAX_LENGTH_TAX_CODE = 13;

export const DURATION_RESEND_OTP = 60;

export const FILE_SIZE_IMAGE = 5 * 1024 * 1024; // 5 MB

export const FILE_SIZE_IMAGE_1MB = 1 * 1024 * 1024; // 5 MB

export const FILE_SIZE_MENU = 50 * 1024 * 1024; // 50 MB

export const MAX_PRICE_SERVICE = 999999999;

export const TIME_STEP_SELECT = 5;

export const LIMIT_IMAGES_UPLOAD = 100;

export const BOOK_DAY_IN_ADVANCE = 364;

export const DISCOUNT_RATE = 100;

export const NUMBER_OF_VOUCHER = 999999;

export const USAGE_LIMIT_VOUCHER = 999;

export const AVERAGE_DURATIONS = Array.from({
    length: (150 - 15) / 15 + 1
}).map((_, index) => 15 * (index + 1));

export const PET_WEIGHT_OPTIONS = Array.from({
    length: 51
}).map((_, index) => index);

export const NULL_VALUE_DISPLAY = '--';

// #endregion

// #region support file type
export const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/png', 'image/webp'];
export const SUPPORTED_FORMATS_MENU_UPLOAD = ['application/pdf'];

// #endregion

// #region shop
export const SHOP_CATEGORY = {
    [SHOP_CATEGORY_TYPE.PetShop]: {
        key: 'category_pet_shop'
    },
    [SHOP_CATEGORY_TYPE.PetHospital]: {
        key: 'category_pet_hospital'
    },
    [SHOP_CATEGORY_TYPE.Retail]: {
        key: 'category_retail'
    }
};

export const SHOP_PICKUP_STATUS = {
    offer: {
        key: 'pickup_offer'
    },
    notOffer: {
        key: 'pickup_not_offer'
    }
};

export const OWNING_SHOP_STATUS = {
    yes: {
        key: 'owning_shop_yes'
    },
    no: {
        key: 'owning_shop_no'
    }
};

export const SHOP_RATING = {
    [SHOP_RATING_TYPE.FiveStars]: {
        key: 'five_stars'
    },
    [SHOP_RATING_TYPE.FourStarsAndUp]: {
        key: 'four_stars_and_up'
    },
    [SHOP_RATING_TYPE.ThreeStarsAndUp]: {
        key: 'three_stars_and_up'
    },
    [SHOP_RATING_TYPE.TwoStarsAndUp]: {
        key: 'two_stars_and_up'
    },
    [SHOP_RATING_TYPE.OneStarAndUp]: {
        key: 'one_stars_and_up'
    },
    [SHOP_RATING_TYPE.NoReview]: {
        key: 'no_review'
    }
};

export const SHOP_STATUS = {
    [SHOP_STATUS_TYPE.WaitApprove]: {
        key: 'shop_status_wait',
        color: 'bg-status-wait-approve',
        value: SHOP_STATUS_TYPE.WaitApprove
    },
    [SHOP_STATUS_TYPE.Active]: {
        key: 'shop_status_active',
        color: 'bg-status-active',
        value: SHOP_STATUS_TYPE.Active
    },
    [SHOP_STATUS_TYPE.Inactive]: {
        key: 'shop_status_inactive',
        color: 'bg-status-inactive',
        value: SHOP_STATUS_TYPE.Inactive
    },
    [SHOP_STATUS_TYPE.DeniedApprove]: {
        key: 'shop_status_denied',
        color: 'bg-status-denied',
        value: SHOP_STATUS_TYPE.DeniedApprove
    },
    [SHOP_STATUS_TYPE.NotCompletedInfo]: {
        key: 'shop_status_not_complete',
        color: 'bg-status-not-complete',
        value: SHOP_STATUS_TYPE.NotCompletedInfo
    }
};

export const SHOP_OWNER = {
    [SHOP_OWNER_TYPE.SelectUser]: {
        key: 'shop_owner_select'
    },
    [SHOP_OWNER_TYPE.NewUser]: {
        key: 'shop_owner_new'
    }
};

export const REPRESENTATIVE_ROLE = {
    [REPRESENTATIVE_ROLE_TYPE.ShopOwner]: {
        key: 'representative_role_shop_owner'
    },
    [REPRESENTATIVE_ROLE_TYPE.Doctor]: {
        key: 'representative_role_doctor'

    },
    [REPRESENTATIVE_ROLE_TYPE.Manager]: {
        key: 'representative_role_manager'
    },
    [REPRESENTATIVE_ROLE_TYPE.Staff]: {
        key: 'representative_role_staff'
    }
};

export const SHOP_SERVICE_NAME = {
    [SHOP_SERVICE_NAME_TYPE.Cut]: {
        key: 'shop_service_cut'
    },
    [SHOP_SERVICE_NAME_TYPE.Shower]: {
        key: 'shop_service_shower'

    },
    [SHOP_SERVICE_NAME_TYPE.Combo]: {
        key: 'shop_service_combo'
    },
    [SHOP_SERVICE_NAME_TYPE.Hotel]: {
        key: 'shop_service_hotel'
    }
};

export const SHOP_SERVICE = {
    [SHOP_SERVICE_TYPE.Cat]: {
        key: 'shop_service_cat'
    },
    [SHOP_SERVICE_TYPE.Dog]: {
        key: 'shop_service_dog'
    },
    [SHOP_SERVICE_TYPE.Medical]: {
        key: 'shop_service_medical'
    },
    [SHOP_SERVICE_TYPE.Other]: {
        key: 'shop_service_other'
    }
};

export const DAYS_OF_WEEK = {
    [DAYS_OF_WEEK_TYPE.Monday]: {
        key: 'days.monday',
        shortCutEn: 'Mon',
        shortCutVi: 'Hai'
    },
    [DAYS_OF_WEEK_TYPE.Tuesday]: {
        key: 'days.tuesday',
        shortCutEn: 'Tue',
        shortCutVi: 'Ba'
    },
    [DAYS_OF_WEEK_TYPE.Wednesday]: {
        key: 'days.wednesday',
        shortCutEn: 'Wed',
        shortCutVi: 'Tư'
    },
    [DAYS_OF_WEEK_TYPE.Thursday]: {
        key: 'days.thursday',
        shortCutEn: 'Thu',
        shortCutVi: 'Năm'
    },
    [DAYS_OF_WEEK_TYPE.Friday]: {
        key: 'days.friday',
        shortCutEn: 'Fri',
        shortCutVi: 'Sáu'
    },
    [DAYS_OF_WEEK_TYPE.Saturday]: {
        key: 'days.saturday',
        shortCutEn: 'Sat',
        shortCutVi: 'Bảy'
    },
    [DAYS_OF_WEEK_TYPE.Sunday]: {
        key: 'days.sunday',
        shortCutEn: 'Sun',
        shortCutVi: 'CN'
    }
};

export const MONTH_OF_YEAR = {
    [MONTH_OF_YEAR_TYPE.January]: {
        shortCutEn: 'Jan',
        fullNameEn: 'January',
        shortCutVi: 'T1',
        fullNameVi: 'Tháng 1'
    },
    [MONTH_OF_YEAR_TYPE.February]: {
        shortCutEn: 'Feb',
        fullNameEn: 'February',
        shortCutVi: 'T2',
        fullNameVi: 'Tháng 2'
    },
    [MONTH_OF_YEAR_TYPE.March]: {
        shortCutEn: 'Mar',
        fullNameEn: 'March',
        shortCutVi: 'T3',
        fullNameVi: 'Tháng 3'
    },
    [MONTH_OF_YEAR_TYPE.April]: {
        shortCutEn: 'Apr',
        fullNameEn: 'April',
        shortCutVi: 'T4',
        fullNameVi: 'Tháng 4'
    },
    [MONTH_OF_YEAR_TYPE.May]: {
        shortCutEn: 'May',
        fullNameEn: 'May',
        shortCutVi: 'T5',
        fullNameVi: 'Tháng 5'
    },
    [MONTH_OF_YEAR_TYPE.June]: {
        shortCutEn: 'Jun',
        fullNameEn: 'June',
        shortCutVi: 'T6',
        fullNameVi: 'Tháng 6'
    },
    [MONTH_OF_YEAR_TYPE.July]: {
        shortCutEn: 'Jul',
        fullNameEn: 'July',
        shortCutVi: 'T7',
        fullNameVi: 'Tháng 7'
    },
    [MONTH_OF_YEAR_TYPE.August]: {
        shortCutEn: 'Aug',
        fullNameEn: 'August',
        shortCutVi: 'T8',
        fullNameVi: 'Tháng 8'
    },
    [MONTH_OF_YEAR_TYPE.September]: {
        shortCutEn: 'Sep',
        fullNameEn: 'September',
        shortCutVi: 'T9',
        fullNameVi: 'Tháng 9'
    },
    [MONTH_OF_YEAR_TYPE.October]: {
        shortCutEn: 'Oct',
        fullNameEn: 'October',
        shortCutVi: 'T10',
        fullNameVi: 'Tháng 10'
    },
    [MONTH_OF_YEAR_TYPE.November]: {
        shortCutEn: 'Nov',
        fullNameEn: 'November',
        shortCutVi: 'T11',
        fullNameVi: 'Tháng 11'
    },
    [MONTH_OF_YEAR_TYPE.December]: {
        shortCutEn: 'Dec',
        fullNameEn: 'December',
        shortCutVi: 'T12',
        fullNameVi: 'Tháng 12'
    }
};

export const PICK_UP_PRICE = {
    [PICK_UP_PRICE_TYPE.Free]: {
        key: 'pick_up_free'
    },
    [PICK_UP_PRICE_TYPE.ChargeByDistance]: {
        key: 'pick_up_charge'
    }
};

// #endregion

// #region user
export const USER_STATUS = {
    [USER_STATUS_TYPE.Active]: {
        key: 'user_status_active',
        color: 'bg-status-active',
        value: USER_STATUS_TYPE.Active
    },
    [USER_STATUS_TYPE.Inactive]: {
        key: 'user_status_inactive',
        color: 'bg-status-inactive',
        value: USER_STATUS_TYPE.Inactive
    }
};

export const USER_STATUS_OPTION_SELECT = {
    all: {
        key: 'all'
    },
    [USER_STATUS_TYPE.Active]: {
        key: 'user_status_active'
    },
    [USER_STATUS_TYPE.Inactive]: {
        key: 'user_status_inactive'
    }
};

// #endregion

// #region Appointment
export const APPOINTMENT_STATUS = {
    [APPOINTMENT_STATUS_TYPE.NotConfirmed]: {
        key: 'appointment_not_confirmed',
        color: 'bg-icon-3',
        value: APPOINTMENT_STATUS_TYPE.NotConfirmed
    },
    [APPOINTMENT_STATUS_TYPE.Confirmed]: {
        key: 'appointment_confirmed',
        color: 'bg-icon-4',
        value: APPOINTMENT_STATUS_TYPE.Confirmed
    },
    [APPOINTMENT_STATUS_TYPE.Completed]: {
        key: 'appointment_completed',
        color: 'bg-icon-5',
        value: APPOINTMENT_STATUS_TYPE.Completed
    },
    [APPOINTMENT_STATUS_TYPE.Cancelled]: {
        key: 'appointment_cancelled',
        color: 'bg-icon-6',
        value: APPOINTMENT_STATUS_TYPE.Cancelled
    }
};

export const APPOINTMENT_CANCELLED_BY = {
    [APPOINTMENT_CANCELLED_BY_TYPE.PetOwner]: {
        key: 'words_title.pet_owner'
    },
    [APPOINTMENT_CANCELLED_BY_TYPE.ShopOwner]: {
        key: 'words_title.shop_owner'
    }
};
// #endregion

// #region Review
export const REVIEW_STATUS = {
    [REVIEW_STATUS_TYPE.Published]: {
        key: 'review_published_status',
        color: 'bg-status-active',
        value: REVIEW_STATUS_TYPE.Published
    },
    [REVIEW_STATUS_TYPE.RequestApproval]: {
        key: 'review_request_status',
        color: 'bg-status-denied',
        value: REVIEW_STATUS_TYPE.RequestApproval
    },
    [REVIEW_STATUS_TYPE.SeedingComments]: {
        key: 'review_seeding_comments_status',
        color: 'bg-status-denied',
        value: REVIEW_STATUS_TYPE.SeedingComments
    }
};

export const COMMENT_IN_REVIEW_STATUS = {
    include: {
        key: 'include'
    },
    notInclude: {
        key: 'not_include'
    }
};
// #endregion

// #region Topic
export const FORUM = {
    [FORUM_TYPE.Career]: {
        key: 'miapet_careers',
        value: FORUM_TYPE.Career
    },
    [FORUM_TYPE.FreeTopic]: {
        key: 'free_topic',
        value: FORUM_TYPE.FreeTopic
    }
};

export const TOPIC = {
    [TOPIC_TYPE.Notice]: {
        key: 'notice',
        value: TOPIC_TYPE.Notice
    },
    [TOPIC_TYPE.Advertisement]: {
        key: 'advertisement',
        value: TOPIC_TYPE.Advertisement
    },
    [TOPIC_TYPE.None]: {
        key: 'none',
        value: TOPIC_TYPE.None
    }
};

export const TOPIC_CREATOR = {
    [TOPIC_CREATOR_TYPE.Admin]: {
        key: 'admin',
        value: TOPIC_CREATOR_TYPE.Admin
    },
    [TOPIC_CREATOR_TYPE.Enduser]: {
        key: 'enduser',
        value: TOPIC_CREATOR_TYPE.Enduser
    }
};

export const TOPIC_STATUS = {
    [TOPIC_STATUS_TYPE.Published]: {
        key: 'published',
        color: 'bg-status-active',
        value: TOPIC_STATUS_TYPE.Published
    },
    [TOPIC_STATUS_TYPE.NotPublished]: {
        key: 'not_published',
        color: 'bg-status-inactive',
        value: TOPIC_STATUS_TYPE.NotPublished
    },
    [TOPIC_STATUS_TYPE.WaitingToApprove]: {
        key: 'waiting_to_approve',
        color: 'bg-status-wait-approve',
        value: TOPIC_STATUS_TYPE.WaitingToApprove
    },
    [TOPIC_STATUS_TYPE.Rejected]: {
        key: 'rejected',
        color: 'bg-status-denied',
        value: TOPIC_STATUS_TYPE.Rejected
    }
};

export const NEWS_CATEGORY = {
    [NEWS_CATEGORY_TYPE.DogCareAndKnowledge]: {
        key: 'dog_care_and_knowledge',
        value: NEWS_CATEGORY_TYPE.DogCareAndKnowledge
    },
    [NEWS_CATEGORY_TYPE.CatCareAndKnowledge]: {
        key: 'cat_care_and_knowledge',
        value: NEWS_CATEGORY_TYPE.CatCareAndKnowledge
    },
    [NEWS_CATEGORY_TYPE.VeterinaryGuidesAndExperiences]: {
        key: 'veterinary_guides_and_experiences',
        value: NEWS_CATEGORY_TYPE.VeterinaryGuidesAndExperiences
    }
};

export const NEWS_STATUS = {
    [NEWS_STATUS_TYPE.NotPublished]: {
        key: 'not_published',
        value: NEWS_STATUS_TYPE.NotPublished
    },
    [NEWS_STATUS_TYPE.Published]: {
        key: 'published',
        value: NEWS_STATUS_TYPE.Published
    }
};

// #endregion

// #start: SEO
export const SEO_ENTITY_TYPE = {
    STATIC: 'static',
    SHOP: 'shops',
    TOPIC: 'forum',
    NEWS: 'news'
};

export const SEO_ENTITY_ID = [
    {
        key: 'home',
        label: 'Homepage'
    },
    {
        key: 'pet-note',
        label: 'Pet Note'
    },
    {
        key: 'near-by',
        label: 'Near by'
    },
    {
        key: 'forum',
        label: 'Forum'
    },
    {
        key: 'dog-knowledge',
        label: 'Dog Care & Knowledge'
    },
    {
        key: 'cat-knowledge',
        label: 'Cat Care & Knowledge'
    },
    {
        key: 'vet-knowledge',
        label: 'Veterinary Guides & Experiences'
    },
    {
        key: 'about-us',
        label: 'About Miapet'
    },
    {
        key: 'miapet-policy',
        label: 'Miapet Policies'
    },
    {
        key: 'miapet-career',
        label: 'Miapet Careers'
    },
    {
        key: 'privacy-policy',
        label: 'Privacy Policy'
    }
];

// #end: SEO

export const FORM_TYPE = {
    CREATE: 'create',
    EDIT: 'edit',
    CLONE: 'clone'
};

export const getShopCategoryName = (categoryId: number) => {
    if (categoryId === SHOP_CATEGORY_TYPE.PetShop) {
        return SHOP_CATEGORY[SHOP_CATEGORY_TYPE.PetShop]?.key ?? 'category_none';
    } else if (categoryId === SHOP_CATEGORY_TYPE.PetHospital) {
        return SHOP_CATEGORY[SHOP_CATEGORY_TYPE.PetHospital]?.key ?? 'category_none';
    } else if (categoryId === SHOP_CATEGORY_TYPE.PetHospital) {
        return SHOP_CATEGORY[SHOP_CATEGORY_TYPE.PetHospital]?.key ?? 'category_none';
    } else if (categoryId === SHOP_CATEGORY_TYPE.Retail) {
        return SHOP_CATEGORY[SHOP_CATEGORY_TYPE.Retail]?.key ?? 'category_none';
    } else {
        return 'category_none';
    }
};
